window.VP = window.VP || {};

(function ($) {
	const VP = window.VP;

	VP.EventsCalendar = {

		// ─── State ────────────────────────────────────────────────────────────
		state: {
			mode: 'discovery',  // 'discovery' | 'browse' | 'search'
			// Per TAXONOMY_CONTRACT.md, filter queries take UUIDs. Both arrays
			// hold tag/category IDs (strings); labels for the UI come from
			// state.categories / state.tags joined by id.
			selectedCategories: [],
			selectedTags: [],
			offset: 0,
			limit: 20,
			total: 0,
			loading: false,
			categories: [],  // full list from /api/categories
			tags: [],        // full list from /api/tags
			lastQuery: '',  // last NL search query, used by load-more
			defaultTo: '',  // default To date (YYYY-MM-DD Eastern) from /api/events/stats latest_end
		},

		// ─── Cached DOM refs ─────────────────────────────────────────────────
		$root: null,
		$bubbles: null,
		$filters: null,
		$filterFrom: null,
		$filterTo: null,
		$tagsFilter: null,
		$filterError: null,
		$results: null,
		$loader: null,
		$status: null,
		$caption: null,
		$tbody: null,
		$empty: null,
		$apiError: null,
		$pagination: null,
		$nlInput: null,
		$nlSubmit: null,
		$seeEvents: null,

		// ─── Init ─────────────────────────────────────────────────────────────
		init: function () {
			const self = this;
			this.$root = $('[data-template="events-calendar"]');
			if (!this.$root.length) return;

			// Cache DOM
			this.$bubbles = $('#js-ec-bubbles');
			this.$filters = $('#js-ec-filters');
			this.$filterFrom = $('#js-ec-from');
			this.$filterTo = $('#js-ec-to');
			this.$tagsFilter = $('#js-ec-tags-filter');
			this.$filterError = $('#js-ec-filter-error');
			this.$results = $('#js-ec-results');
			this.$loader = $('#js-ec-loader');
			this.$status = $('#js-ec-status');
			this.$caption = $('#js-ec-caption');
			this.$tbody = $('#js-ec-tbody');
			this.$empty = $('#js-ec-empty');
			this.$apiError = $('#js-ec-api-error');
			this.$pagination = $('#js-ec-pagination');
			this.$nlInput = $('#js-ec-nl-input');
			this.$nlSubmit = $('.js-ec-nl-submit');
			this.$seeEvents = $('.js-ec-see-events');

			// Bind events
			this.$seeEvents.on('click', this.onSeeEventsClick.bind(this));

			// Date fields: click anywhere on the field opens the native picker
			this.$filters.on('click', '.vp-events-cal__date-field', function () {
				self._setEngaged('filters');
				var input = $(this).find('.vp-events-cal__date-input')[0];
				try { if (input.showPicker) { input.showPicker(); } else { input.click(); } } catch (e) { /* showPicker can throw if not user-initiated */ }
			});

			// Date fields: block keyboard input — picker only, no typing
			this.$filters.on('keydown', '.vp-events-cal__date-input', function (e) {
				e.preventDefault();
			});

			// Date fields: update the visible label when a date is selected
			this.$filters.on('change', '.vp-events-cal__date-input', function () {
				var val  = $(this).val();
				var $lbl = $(this).closest('.vp-events-cal__date-field').find('.vp-events-cal__date-label');
				if (!$lbl.data('default')) { $lbl.data('default', $lbl.text()); }
				$lbl.text(val
					? new Date(val + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
					: $lbl.data('default')
				);
			});

			// Tags dropdown: toggle open/closed
			this.$filters.on('click', '.js-ec-tags-field', function (e) {
				e.stopPropagation();
				self._setEngaged('filters');
				var $field = $(this);
				var opening = !$field.hasClass('is-open');
				$field.toggleClass('is-open', opening);
				$field.attr('aria-expanded', String(opening));
			});

			// Tags dropdown: close when clicking outside
			$(document).on('click.ec-tags', function () {
				self.$filters.find('.js-ec-tags-field').removeClass('is-open').attr('aria-expanded', 'false');
			});

			// Tags: checkbox change → update state + placeholder
			this.$filters.on('change', '.js-ec-tag-cb', function (e) {
				e.stopPropagation();
				self._setEngaged('filters');
				self.onTagCheckboxChange(e);
				self._updateTagsPlaceholder();
			});
			this.$nlSubmit.on('click', this.onNlSubmit.bind(this));
			this.$nlInput.on('keydown', function (e) {
				if (e.key === 'Enter') this.onNlSubmit(e);
			}.bind(this));

			// Engaging the conversational search dims the filters (and vice versa).
			this.$nlInput.on('focus', function () {
				self._setEngaged('search');
			});

			// Typing a free-text search deselects any chosen categories/tags/dates —
			// the two filter modes are mutually exclusive.
			this.$nlInput.on('input', function () {
				self._setEngaged('search');
				if (self.state.selectedCategories.length) {
					self.state.selectedCategories = [];
					self.syncBubbleStates();
				}
				if (self.state.selectedTags.length) {
					self.state.selectedTags = [];
					self.syncFilterTagCheckboxes();
					self._updateTagsPlaceholder();
				}
				self._clearDateFilters();
			});
			this.$pagination.on('click', '.js-ec-prev-page', this.onPrevPage.bind(this));
			this.$pagination.on('click', '.js-ec-next-page', this.onNextPage.bind(this));
			this.$results.on('click', '.js-ec-retry', this.onRetry.bind(this));
			this.$tbody.on('click', 'tr.vp-events-cal__row', this.onRowClick.bind(this));

			// Pin From date to today (and block past dates) before any user interaction.
			this._ensureFromIsToday();
			// To picker shares the same lower bound — no past dates.
			this.$filterTo.attr('min', this._todayEastern());

			// Kick off
			this.fetchCategories();
			this.fetchTags();
			this.fetchStats();
		},

		// ─── Timezone normalization ─────────────────────────────────────────────
		// Backend sends UTC ISO strings. We convert to naive Eastern at ingest;
		// downstream code only string-slices, never re-parses through Date.
		_easternFmt: null,
		_getEasternFmt: function () {
			if (!this._easternFmt) {
				this._easternFmt = new Intl.DateTimeFormat('en-CA', {
					timeZone: 'America/New_York',
					year: 'numeric', month: '2-digit', day: '2-digit',
					hour: '2-digit', minute: '2-digit', second: '2-digit',
					hour12: false,
				});
			}
			return this._easternFmt;
		},
		toEastern: function (utcStr) {
			if (!utcStr) return utcStr;
			const d = new Date(utcStr);
			if (isNaN(d.getTime())) return utcStr;
			const parts = this._getEasternFmt().formatToParts(d);
			const get = function (t) {
				const p = parts.find(function (x) { return x.type === t; });
				return p ? p.value : '00';
			};
			// Intl can emit hour=24 at midnight; coerce to 00.
			const hour = get('hour') === '24' ? '00' : get('hour');
			return get('year') + '-' + get('month') + '-' + get('day') + 'T' +
				hour + ':' + get('minute') + ':' + get('second');
		},
		// Today's date in America/New_York as YYYY-MM-DD.
		_todayEastern: function () {
			const fmt = new Intl.DateTimeFormat('en-CA', {
				timeZone: 'America/New_York',
				year: 'numeric', month: '2-digit', day: '2-digit',
			});
			const parts = fmt.formatToParts(new Date());
			const get = function (t) {
				const p = parts.find(function (x) { return x.type === t; });
				return p ? p.value : '01';
			};
			return get('year') + '-' + get('month') + '-' + get('day');
		},
		// Block past-date selection on the From picker; pin value to today if missing or stale.
		_ensureFromIsToday: function () {
			const today = this._todayEastern();
			this.$filterFrom.attr('min', today);
			const cur = this.$filterFrom.val();
			if (!cur || cur < today) {
				this.$filterFrom.val(today).trigger('change');
			}
		},
		// Dim whichever of the two mutually-exclusive experiences (filters vs
		// conversational search) is NOT being used, so the user can see which
		// area they're interacting with. mode: 'filters' | 'search'. Dimmed
		// controls stay clickable — engaging a dimmed area flips the state.
		_setEngaged: function (mode) {
			this.$filters
				.toggleClass('is-filters-engaged', mode === 'filters')
				.toggleClass('is-search-engaged', mode === 'search');
		},
		// Reset From to today and To to its default (latest known event end,
		// empty if stats never loaded) — used when a free-text search takes
		// over from the date/category/tag filters.
		_clearDateFilters: function () {
			const today = this._todayEastern();
			if (this.$filterFrom.val() !== today) {
				this.$filterFrom.val(today).trigger('change');
			}
			if (this.$filterTo.val() !== this.state.defaultTo) {
				this.$filterTo.val(this.state.defaultTo).trigger('change');
			}
		},
		_normalizeEvents: function (data) {
			if (!data || !data.events) return;
			const self = this;
			data.events.forEach(function (event) {
				if (event.starts_at) event.starts_at = self.toEastern(event.starts_at);
				if (event.ends_at)   event.ends_at   = self.toEastern(event.ends_at);
				if (event.occurrences) {
					event.occurrences.forEach(function (occ) {
						if (occ.starts_at) occ.starts_at = self.toEastern(occ.starts_at);
						if (occ.ends_at)   occ.ends_at   = self.toEastern(occ.ends_at);
					});
				}
			});
		},
		_fmtDate: function (ymd) {
			if (!ymd || !/^\d{4}-\d{2}-\d{2}/.test(ymd)) return ymd || '';
			const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
			const m = parseInt(ymd.slice(5, 7), 10);
			const d = parseInt(ymd.slice(8, 10), 10);
			return months[m - 1] + ' ' + d + ', ' + ymd.slice(0, 4);
		},
		_fmtTime: function (hm) {
			if (!hm || !/^\d{2}:\d{2}/.test(hm)) return '';
			const h = parseInt(hm.slice(0, 2), 10);
			const m = parseInt(hm.slice(3, 5), 10);
			if (h === 12 && m === 0) return 'noon';
			if (h === 0  && m === 0) return 'midnight';
			const period = h >= 12 ? 'p.m.' : 'a.m.';
			const h12 = h === 0 ? 12 : (h > 12 ? h - 12 : h);
			const minStr = m === 0 ? '' : ':' + hm.slice(3, 5);
			return h12 + minStr + ' ' + period;
		},
		_fmtPrice: function (n) {
			if (n == null) return '';
			const num = Number(n);
			if (isNaN(num)) return String(n);
			return '$' + num.toFixed(2);
		},

		// ─── API layer ───────────────────────────────────────────────────────────
		fetchCategories: function () {
			const self = this;
			fetch('/index.php?ajax=events_public_categories')
				.then(function (res) {
					if (!res.ok) throw new Error('HTTP ' + res.status);
					return res.json();
				})
				.then(function (cats) {
					self.state.categories = cats || [];
					self.renderBubbles(self.state.categories);
				})
				.catch(function (err) {
					console.error('[EventsCalendar] fetchCategories failed:', err);
				});
		},

		fetchTags: function () {
			const self = this;
			fetch('/index.php?ajax=events_public_tags')
				.then(function (res) {
					if (!res.ok) throw new Error('HTTP ' + res.status);
					return res.json();
				})
				.then(function (tags) {
					self.state.tags = tags || [];
					self.renderFilterTags();
				})
				.catch(function (err) {
					console.error('[EventsCalendar] fetchTags failed:', err);
				});
		},

		// Default the To picker to the furthest-in-the-future event end date.
		// latest_end arrives as UTC; convert once at the boundary per the
		// timezone rule. On failure or no usable date, To stays empty.
		fetchStats: function () {
			const self = this;
			fetch('/index.php?ajax=events_public_stats')
				.then(function (res) {
					if (!res.ok) throw new Error('HTTP ' + res.status);
					return res.json();
				})
				.then(function (stats) {
					if (!stats || !stats.latest_end) return;
					const latest = self.toEastern(stats.latest_end).slice(0, 10);
					if (!/^\d{4}-\d{2}-\d{2}$/.test(latest)) return;
					if (latest < self._todayEastern()) return;
					self.state.defaultTo = latest;
					// Cap both pickers at the last known event end so the native
					// picker can't offer years beyond where events exist.
					self.$filterFrom.attr('max', latest);
					self.$filterTo.attr('max', latest);
					if (!self.$filterTo.val()) {
						self.$filterTo.val(latest).trigger('change');
					}
				})
				.catch(function (err) {
					console.error('[EventsCalendar] fetchStats failed:', err);
				});
		},

		buildApiParams: function () {
			// Always send date_from; default to today if user cleared it.
			this._ensureFromIsToday();
			const params = new URLSearchParams();
			this.state.selectedCategories.forEach(function (cat) {
				params.append('categories', cat);
			});
			this.state.selectedTags.forEach(function (tag) {
				params.append('tags', tag);
			});
			const from = this.$filterFrom.val() || this._todayEastern();
			const to   = this.$filterTo.val();
			params.set('date_from', from);
			if (to) params.set('date_to', to);
			params.set('limit',  this.state.limit);
			params.set('offset', this.state.offset);
			return params;
		},

		fetchEvents: function () {
			if (this.state.loading) return;
			const self = this;
			self.setLoading(true);
			self.showApiError(false);

			const url = '/index.php?ajax=events_public_list&' + self.buildApiParams().toString();

			fetch(url)
				.then(function (res) {
					if (res.status === 422) {
						self.showFilterError('Please check your date range. From must be before To.');
						self.setLoading(false);
						return null;
					}
					if (!res.ok) throw new Error('HTTP ' + res.status);
					return res.json();
				})
				.then(function (data) {
					if (!data) return;
					self._normalizeEvents(data);
					self.renderEvents(data);
					self.setLoading(false);
				})
				.catch(function (err) {
					console.error('[EventsCalendar] fetchEvents failed:', err);
					self.showApiError(true);
					self.setLoading(false);
				});
		},

		fetchSearch: function (query) {
			if (this.state.loading) return;
			const self = this;
			self.setLoading(true);
			self.showApiError(false);

			const params = new URLSearchParams();
			params.set('q',      query);
			params.set('limit',  self.state.limit);
			params.set('offset', self.state.offset);

			fetch('/index.php?ajax=events_public_search&' + params.toString())
				.then(function (res) {
					if (!res.ok) throw new Error('HTTP ' + res.status);
					return res.json();
				})
				.then(function (data) {
					// API returns null when no semantic matches found
					const safe = data || { events: [], total: 0, limit: self.state.limit, offset: 0 };
					if (!safe.events) safe.events = [];
					self._normalizeEvents(safe);
					self.renderEvents(safe);
					self.setLoading(false);
					self._resetNlBar();
				})
				.catch(function (err) {
					console.error('[EventsCalendar] fetchSearch failed:', err);
					self.showApiError(true);
					self.setLoading(false);
					self._resetNlBar();
				});
		},

		_resetNlBar: function () {
			this.$nlInput.prop('disabled', false);
			this.$nlSubmit
				.prop('disabled', false)
				.html('<span aria-hidden="true">→</span>');
		},

		// Abandon any in-progress/completed free-text search — used when the
		// admin goes back to category/tag/date filters via "See Your Events".
		_clearNlSearch: function () {
			this.state.lastQuery = '';
			this.$nlInput.val('');
			this._resetNlBar();
		},

		// ─── Render layer ────────────────────────────────────────────────────────
		BUBBLE_COLORS: ['#ff5200', '#e6fb00', '#00a4b5', '#fe3180', '#20c68e', '#fe3ff5', '#8b53dd', '#ffc704'],

		// Layout panels: hero-copy (top-left) and hero-cta (bottom-right) each
		// occupy this many grid columns. Bubbles fill the remaining cells.
		PANEL_W: 2,

		// Given total bubble count N (All Events + active categories), return
		// the row breakdown. Algorithm:
		//   N=1..2   → top only (1 or 2 bubbles)
		//   N=3..4   → top=2, bottom=N-2 (no middle row)
		//   N=5..9   → top=2, middle=N-4, bottom=2
		//   N=10     → top=3, middle=4, bottom=3
		//   N≥11     → top=3, bottom=3, middle=N-6 (wraps when middle > 5)
		// Middle wrapping:
		//   ≤5   → 1 row
		//   6–10 → balanced 2 rows, larger half on top (6→3+3, 7→4+3, 8→4+4,
		//          9→5+4, 10→5+5)
		//   >10  → cap at 5 per row (11→5+5+1, 12→5+5+2, …, 15→5+5+5,
		//          16→5+5+5+1)
		_computeRowLayout: function (N) {
			if (N <= 0) return { top: 0, middleRows: [], bottom: 0 };
			if (N === 1) return { top: 1, middleRows: [], bottom: 0 };
			if (N === 2) return { top: 2, middleRows: [], bottom: 0 };
			if (N === 3) return { top: 2, middleRows: [], bottom: 1 };
			if (N === 4) return { top: 2, middleRows: [], bottom: 2 };
			if (N <= 9)  return { top: 2, middleRows: [N - 4], bottom: 2 };
			if (N === 10) return { top: 3, middleRows: [4], bottom: 3 };
			// N ≥ 11
			var mid = N - 6;
			return { top: 3, middleRows: this._splitMiddle(mid), bottom: 3 };
		},

		_splitMiddle: function (M) {
			if (M <= 5) return [M];
			if (M <= 10) {
				var head = Math.ceil(M / 2);
				return [head, M - head];
			}
			var rows = [];
			while (M > 0) {
				var n = Math.min(5, M);
				rows.push(n);
				M -= n;
			}
			return rows;
		},

		renderBubbles: function (cats) {
			const self = this;
			this.$bubbles.empty();

			const activeCats = cats.filter(function (c) { return c.active !== false; });
			const bubbles    = [{ isAll: true }].concat(activeCats);
			const N          = bubbles.length;

			const layout = this._computeRowLayout(N);
			const panelW = this.PANEL_W;

			// Grid columns: wide enough for the widest row including its panel.
			var maxRow = Math.max(layout.top, layout.bottom);
			layout.middleRows.forEach(function (r) { maxRow = Math.max(maxRow, r); });
			const gridCols = Math.max(layout.top + panelW, layout.bottom + panelW, maxRow, 4);

			// Grid rows: row 1 = top (always present for hero-copy panel),
			// then middle sub-rows, then bottom row (always present for hero-cta).
			const totalGridRows = 1 + layout.middleRows.length + 1;

			// Compute placements: (col, row) for each bubble in document order.
			const placements = [];
			let idx = 0;

			// Top: right of hero-copy panel, left-aligned starting at col panelW+1.
			for (var t = 0; t < layout.top && idx < N; t++, idx++) {
				placements.push({ col: panelW + 1 + t, row: 1 });
			}

			// Middle sub-rows: each centered horizontally in gridCols.
			layout.middleRows.forEach(function (rowSize, mi) {
				var rowNum  = 2 + mi;
				var startCol = Math.floor((gridCols - rowSize) / 2) + 1;
				for (var m = 0; m < rowSize && idx < N; m++, idx++) {
					placements.push({ col: startCol + m, row: rowNum });
				}
			});

			// Bottom: left of hero-cta panel, left-aligned starting at col 1.
			var bottomRow = 1 + layout.middleRows.length + 1;
			for (var b = 0; b < layout.bottom && idx < N; b++, idx++) {
				placements.push({ col: 1 + b, row: bottomRow });
			}

			// Configure the hero grid: cols, rows, and the hero-cta panel position.
			// Inline styles let the layout adapt to N without bloating the SCSS.
			const $hero = $('#js-ec-hero');
			$hero.removeClass('is-extended'); // legacy class, no longer needed
			const heroEl = $hero[0];
			if (heroEl) {
				heroEl.style.gridTemplateColumns = 'repeat(' + gridCols + ', 1fr)';
				// Top + bottom rows are "outer" (var(--ec-row-outer)); middle sub-rows
				// share var(--ec-row-middle) split evenly.
				var rowParts = ['var(--ec-row-outer)'];
				for (var i = 0; i < layout.middleRows.length; i++) {
					rowParts.push('var(--ec-row-middle-sub)');
				}
				rowParts.push('var(--ec-row-outer)');
				heroEl.style.gridTemplateRows = rowParts.join(' ');
				heroEl.style.setProperty('--cta-col-start', String(gridCols - panelW + 1));
				heroEl.style.setProperty('--cta-col-end',   String(gridCols + 1));
				heroEl.style.setProperty('--cta-row',       String(totalGridRows));

				// Bubble size shrinks as the grid widens so 240px bubbles don't
				// overflow narrower columns at higher N.
				var bubblePx = gridCols >= 5 ? 200 : 240;
				heroEl.style.setProperty('--bubble-size', bubblePx + 'px');
			}

			// All Events visual defaults (image + color) come from WordPress (wp_options),
			// not the backend. Inlined by page-events-calendar.php as window.vpAllEventsCategory.
			var allEventsCfg = (typeof window !== 'undefined' && window.vpAllEventsCategory) || {};
			var allHasCustom = !!(allEventsCfg.image_url || allEventsCfg.color);

			bubbles.forEach(function (item, i) {
				var slot = placements[i];
				if (!slot) return;

				// Apply the legacy "solid red, no image" treatment only when no custom
				// All Events config exists. With config present, the bubble renders
				// exactly like a regular category (gradient overlay + own color).
				var useLegacyAllEvents = item.isAll && !allHasCustom;

				var $btn = $('<button>', {
					type: 'button',
					class: 'vp-events-cal__bubble js-ec-bubble' + (useLegacyAllEvents ? ' is-all-events' : ''),
					// Per TAXONOMY_CONTRACT.md, filter values are UUIDs. The
					// bubble's data-category holds the category id; visible
					// label below still renders item.name.
					'data-category': item.isAll ? '' : (item.id || ''),
				});

				$btn.css({ gridColumn: slot.col, gridRow: slot.row });

				if (item.isAll) {
					if (allEventsCfg.image_url) {
						$btn[0].style.setProperty('--bubble-img', 'url("' + allEventsCfg.image_url + '")');
					}
					if (allEventsCfg.color) {
						$btn[0].style.setProperty('--bubble-color', allEventsCfg.color);
						// Solid color bubble when no image is set — override the #444 fallback
						if (!allEventsCfg.image_url) {
							$btn[0].style.backgroundColor = allEventsCfg.color;
						}
					}
				} else {
					if (item.image_url) {
						$btn[0].style.setProperty('--bubble-img', 'url("' + item.image_url + '")');
					}
					var color = item.color || self.BUBBLE_COLORS[(i - 1) % self.BUBBLE_COLORS.length];
					$btn[0].style.setProperty('--bubble-color', color);
				}

				$btn.append($('<span>', {
					class: 'vp-events-cal__bubble-label',
					text: item.isAll ? 'All Events' : (item.name || ''),
				}));

				self.$bubbles.append($btn);
			});

			this.$bubbles.off('click.ec').on('click.ec', '.js-ec-bubble', function (e) {
				self.onCategoryClick(e);
			});

			this.syncBubbleStates();
		},
		renderEventRow: function (event) {
			let dateStr = '';
			let timeStr = '';

			if (event.ongoing) {
				dateStr = 'Ongoing';
			} else if (event.upcoming) {
				dateStr = 'Upcoming';
			} else if (event.starts_at) {
				// Single-event range from top-level starts_at/ends_at.
				const startYmd = event.starts_at.slice(0, 10);
				const endYmd   = event.ends_at ? event.ends_at.slice(0, 10) : startYmd;
				const startFmt = this._fmtDate(startYmd);
				const endFmt   = this._fmtDate(endYmd);
				dateStr = startYmd === endYmd ? startFmt : startFmt + ' – ' + endFmt;
				if (event.has_start_time) {
					timeStr = this._fmtTime(event.starts_at.slice(11, 16));
				}
			} else {
				// Multi-occurrence event: derive from occurrence list.
				const occs  = event.occurrences || [];
				const first = occs[0];
				const last  = occs[occs.length - 1];

				if (first && first.starts_at) {
					const startYmd = first.starts_at.slice(0, 10);
					const endYmd   = (last && last.starts_at) ? last.starts_at.slice(0, 10) : startYmd;
					const startFmt = this._fmtDate(startYmd);
					const endFmt   = this._fmtDate(endYmd);
					dateStr = startYmd === endYmd ? startFmt : startFmt + ' – ' + endFmt;

					if (first.has_start_time) {
						timeStr = this._fmtTime(first.starts_at.slice(11, 16));
					}

					if (occs.length > 1) {
						dateStr += ' <span class="vp-events-cal__multi-dates">+' +
							(occs.length - 1) + ' more</span>';
					}
				}
			}

			const nameHtml =
				'<span class="vp-events-cal__row-toggle" aria-hidden="true">&#x203A;</span>' +
				this._escHtml(event.title);

			const $tr = $('<tr>')
				.addClass('vp-events-cal__row')
				.attr({ 'data-event-id': event.event_id, 'aria-expanded': 'false', 'tabindex': '0' })
				.html(
					'<td class="vp-events-cal__col-name"  data-label="Event">' + nameHtml + '</td>' +
					'<td class="vp-events-cal__col-venue" data-label="Location">' + this._escHtml(event.venue || event.address || '') + '</td>' +
					'<td class="vp-events-cal__col-date"  data-label="Date">' + dateStr + '</td>' +
					'<td class="vp-events-cal__col-time"  data-label="Time">' + timeStr + '</td>'
				);

			$tr.data('event', event);
			return $tr;
		},

		renderEvents: function (data) {
			const events   = data.events || [];
			// Use the API's echoed values — protects against stale state from any
			// in-flight pagination clicks while a new search was being prepared.
			const limit    = data.limit  || this.state.limit;
			const offset   = data.offset != null ? data.offset : this.state.offset;
			const apiTotal = data.total || 0;
			this.state.offset = offset;

			// Stabilise the page count so "Page X of Y" doesn't drift while
			// the visitor is paginating. We trust the API's `total` only on
			// the first page of a result set (offset === 0); subsequent
			// pages reuse the captured total. We still clamp downward if a
			// later page reveals we've run off the end — but we never grow
			// the total mid-navigation, which is what produced reports like
			// "1 of 5" → "3 of 8".
			if (offset === 0) {
				this.state.total = apiTotal || events.length;
			} else if (events.length === 0) {
				// Past the end — clamp to the last known boundary.
				this.state.total = offset;
			} else if (events.length < limit) {
				// Short page — this is the last page; pin the total here.
				this.state.total = offset + events.length;
			}
			// else: full page mid-set, keep state.total as captured on page 0.
			this.$tbody.empty();

			if (events.length === 0) {
				this.$empty.removeAttr('hidden');
				this.$pagination.attr('hidden', '');
				this.$caption.text('Events — 0 results');
				this.$status.text('No events found.');
				return;
			}

			this.$empty.attr('hidden', '');

			const self = this;
			events.forEach(function (event) {
				self.$tbody.append(self.renderEventRow(event));
			});

			const currentPage = Math.floor(this.state.offset / this.state.limit) + 1;
			const totalPages  = Math.ceil(this.state.total / this.state.limit);
			this.$caption.text(
				'Events — ' + this.state.total + ' result' + (this.state.total !== 1 ? 's' : '') +
				(totalPages > 1 ? ' — page ' + currentPage + ' of ' + totalPages : '')
			);
			this.$status.text('Showing page ' + currentPage + ' of ' + totalPages + '.');
			this.renderPagination();
		},

		renderPagination: function () {
			const currentPage = Math.floor(this.state.offset / this.state.limit) + 1;
			const totalPages  = Math.ceil(this.state.total / this.state.limit);

			if (totalPages <= 1) {
				this.$pagination.attr('hidden', '');
				return;
			}

			this.$pagination.removeAttr('hidden').html(
				'<button type="button" class="vp-events-cal__page-btn js-ec-prev-page"' +
					(currentPage <= 1 ? ' disabled' : '') + '>&#x2190; Prev</button>' +
				'<span class="vp-events-cal__page-indicator">Page ' + currentPage + ' of ' + totalPages + '</span>' +
				'<button type="button" class="vp-events-cal__page-btn js-ec-next-page"' +
					(currentPage >= totalPages ? ' disabled' : '') + '>Next &#x2192;</button>'
			);
		},

		expandRow: function ($row, event) {
			// Collapse if already open
			const $existing = $row.next('.vp-events-cal__detail-row');
			if ($existing.length) {
				$existing.remove();
				$row.removeClass('is-expanded').attr('aria-expanded', 'false');
				$row.trigger('focus');
				return;
			}

			const self = this;
			const parts = [];

			// Badges (built-in flags + per-event tags from the API).
			// Event responses embed a TagSummary array (id, name, color) —
			// render each as a colored chip in the same badge row.
			// "Free" is intentionally NOT a built-in badge: the "Free" tag from
			// Manage Tags covers it, and rendering both showed "Free" twice.
			// event.is_free still drives whether prices are displayed below.
			let badgesHtml = '';
			if (event.is_kid_friendly === true) {
				badgesHtml += '<span class="vp-events-cal__badge vp-events-cal__badge--kids">Kid-friendly</span>';
			}
			if (Array.isArray(event.tags)) {
				event.tags.forEach(function (tag) {
					if (!tag || !tag.name) return;
					var bg = tag.color || '#9CA3AF';
					var fg = self._contrastTextColor(bg);
					badgesHtml += '<span class="vp-events-cal__badge vp-events-cal__badge--tag"' +
						' style="background:' + self._escHtml(bg) + ';color:' + fg + ';">' +
						self._escHtml(tag.name) +
						'</span>';
				});
			}
			if (badgesHtml) {
				parts.push('<div class="vp-events-cal__badges">' + badgesHtml + '</div>');
			}

			// Event image (event.image_url) is intentionally not rendered for now.

			// Description — full text
			if (event.description) {
				parts.push('<p class="vp-events-cal__detail-desc">' + this._escHtml(event.description) + '</p>');
			}

			// URL with globe icon — manual events have no real source URL to show
			if (event.canonical_url && !event.is_manual) {
				parts.push(
					'<p class="vp-events-cal__detail-line">' +
					'<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round">' +
					'<circle cx="12" cy="12" r="9"/><line x1="3" y1="12" x2="21" y2="12"/>' +
					'<path d="M12 3 a14 14 0 0 1 0 18 a14 14 0 0 1 0 -18"/></svg>' +
					'<a href="' + event.canonical_url + '" target="_blank" rel="noopener">' + this._escHtml(this._displayUrl(event.canonical_url)) + '</a>' +
					'</p>'
				);
			}

			// Address with pin icon
			if (event.address) {
				parts.push(
					'<p class="vp-events-cal__detail-line">' +
					'<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round">' +
					'<path d="M12 21s-7-7.5-7-12a7 7 0 0 1 14 0c0 4.5-7 12-7 12z"/><circle cx="12" cy="9" r="2.5"/></svg>' +
					'<span>' + this._escHtml(event.address) + '</span>' +
					'</p>'
				);
			}

			// Occurrences — flat single-line items
			if (event.occurrences && event.occurrences.length) {
				const items = event.occurrences.map(function (occ) {
					let occDate = '';
					if (occ.ongoing) {
						occDate = 'Ongoing';
					} else if (occ.upcoming) {
						occDate = 'Upcoming';
					} else if (occ.starts_at) {
						occDate = self._fmtDate(occ.starts_at.slice(0, 10));
						if (occ.has_start_time) {
							occDate += ' · ' + self._fmtTime(occ.starts_at.slice(11, 16));
						}
					}

					let priceStr = '';
					if (!event.is_free && occ.price) {
						if (occ.price.min != null || occ.price.max != null) {
							priceStr = (occ.price.min != null ? self._fmtPrice(occ.price.min) : '') +
								(occ.price.min != null && occ.price.max != null ? '–' : '') +
								(occ.price.max != null ? self._fmtPrice(occ.price.max) : '');
						} else if (occ.price.info) {
							priceStr = self._escHtml(occ.price.info);
						}
					}

					const isSoldOut   = occ.status === 'sold_out';
					const isCancelled = occ.status === 'cancelled';
					let statusHtml = '';
					if (isSoldOut) {
						statusHtml = '<span class="vp-events-cal__badge vp-events-cal__badge--soldout">Sold Out</span>';
					} else if (isCancelled) {
						statusHtml = '<span class="vp-events-cal__badge vp-events-cal__badge--cancelled">Cancelled</span>';
					} else if (occ.ticket_url || event.ticket_url) {
						// The API already falls back to the event link for occurrences
						// without one; mirror that here in case an older payload doesn't.
						statusHtml = '<a class="vp-events-cal__detail-cta" href="' + self._escHtml(occ.ticket_url || event.ticket_url) + '" target="_blank" rel="noopener">Get Tickets &#x2192;</a>';
					}

					return '<li class="vp-events-cal__occ' + (isSoldOut || isCancelled ? ' is-dimmed' : '') + '">' +
						'<span class="vp-events-cal__occ-date">' + self._escHtml(occDate) + '</span>' +
						(priceStr ? '<span class="vp-events-cal__occ-price">' + priceStr + '</span>' : '') +
						statusHtml +
						'</li>';
				});
				parts.push('<ul class="vp-events-cal__occurrences">' + items.join('') + '</ul>');
			} else if (event.ticket_url) {
				// No occurrences (the common case for manually entered events, and
				// for scraped events without dates): the ticket link lives on the
				// event itself. Use the event-level ticket_url, never occurrences[0].
				parts.push(
					'<p class="vp-events-cal__detail-tickets">' +
					'<a class="vp-events-cal__detail-cta" href="' + this._escHtml(event.ticket_url) + '" target="_blank" rel="noopener">Get Tickets &#x2192;</a>' +
					'</p>'
				);
			}

			const $detail = $('<tr>')
				.addClass('vp-events-cal__detail-row')
				.html('<td colspan="4"><div class="vp-events-cal__detail">' + parts.join('') + '</div></td>');

			$row.addClass('is-expanded').attr('aria-expanded', 'true');
			$row.after($detail);
		},

		_updateTagsPlaceholder: function () {
			var n    = this.state.selectedTags.length;
			var text = n > 0 ? n + ' tag' + (n === 1 ? '' : 's') + ' selected' : 'Tags';
			this.$filters.find('.vp-events-cal__tags-placeholder').text(text);
		},

		renderFilterTags: function () {
			const self = this;
			this.$tagsFilter.empty();
			const activeTags = this.state.tags.filter(function (tag) { return tag.active !== false; });
			if (!activeTags.length) return;
			activeTags.forEach(function (tag) {
				// Per TAXONOMY_CONTRACT.md, the checkbox value is the tag's
				// UUID; the rendered label is tag.name (preserving casing).
				const domId   = 'ec-tag-cb-' + self._escHtml(String(tag.id));
				const checked = self.state.selectedTags.indexOf(tag.id) !== -1;
				// Tags have a color from the API (defaults to neutral gray
				// when unset). Render a small swatch so admins recognize the
				// styling that will appear on event chips elsewhere.
				const swatch = tag.color
					? '<span class="vp-events-cal__tag-swatch" style="background:' + self._escHtml(tag.color) + ';"></span>'
					: '';
				self.$tagsFilter.append(
					'<label class="vp-events-cal__tag-cb-label" for="' + domId + '">' +
					'<input type="checkbox" id="' + domId + '" class="js-ec-tag-cb"' +
					' value="' + self._escHtml(tag.id) + '"' +
					(checked ? ' checked' : '') + ' />' +
					swatch +
					'<span class="vp-events-cal__tag-cb-name">' + self._escHtml(tag.name) + '</span>' +
					'</label>'
				);
			});
			this._updateTagsPlaceholder();
		},

		syncFilterTagCheckboxes: function () {
			const self = this;
			this.$tagsFilter.find('.js-ec-tag-cb').prop('checked', false);
			this.state.selectedTags.forEach(function (tagId) {
				self.$tagsFilter.find('.js-ec-tag-cb[value="' + tagId + '"]').prop('checked', true);
			});
		},

		syncBubbleStates: function () {
			const self = this;
			if (this.state.selectedCategories.length === 0) {
				this.$bubbles.find('.js-ec-bubble').removeClass('is-active');
				this.$bubbles.find('[data-category=""]').addClass('is-active');
			} else {
				this.$bubbles.find('[data-category=""]').removeClass('is-active');
				this.$bubbles.find('.js-ec-bubble[data-category]').each(function () {
					const cat = $(this).data('category');
					$(this).toggleClass('is-active', cat !== '' && self.state.selectedCategories.indexOf(cat) !== -1);
				});
			}
		},

		onTagCheckboxChange: function (e) {
			const name = $(e.currentTarget).val();
			const idx  = this.state.selectedTags.indexOf(name);
			if ($(e.currentTarget).prop('checked')) {
				if (idx === -1) this.state.selectedTags.push(name);
			} else {
				if (idx !== -1) this.state.selectedTags.splice(idx, 1);
			}
		},

		// Maps selected category/tag UUIDs to their display names (comma-joined)
		_namesFor: function (ids, list) {
			const byId = {};
			list.forEach(function (item) { byId[item.id] = item.name; });
			return ids.map(function (id) { return byId[id] || id; }).join(',');
		},

		// Some partner sites use a client-side router (e.g. "/#/event/slug") whose
		// deep-link path is meaningless to a reader — show just the site's origin.
		_displayUrl: function (url) {
			var hashIdx = String(url).indexOf('/#/');
			return hashIdx === -1 ? url : url.slice(0, hashIdx);
		},

		// Minimal HTML escape for JS-generated markup
		_escHtml: function (str) {
			return String(str)
				.replace(/&/g, '&amp;')
				.replace(/</g, '&lt;')
				.replace(/>/g, '&gt;')
				.replace(/"/g, '&quot;')
				.replace(/'/g, '&#39;');
		},

		// Pick a readable foreground (black/white) for a given hex background.
		// Uses the standard sRGB luminance approximation; threshold tuned so
		// VP red/orange stay white-text while pale yellows flip to black.
		_contrastTextColor: function (hex) {
			if (!hex) return '#fff';
			var h = String(hex).replace('#', '');
			if (h.length === 3) {
				h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2];
			}
			if (!/^[0-9a-fA-F]{6}$/.test(h)) return '#fff';
			var r = parseInt(h.slice(0, 2), 16);
			var g = parseInt(h.slice(2, 4), 16);
			var b = parseInt(h.slice(4, 6), 16);
			var lum = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
			return lum > 0.6 ? '#111' : '#fff';
		},

		// ─── Interaction handlers ─────────────────────────────────────────────────
		onCategoryClick: function (e) {
			// Category clicks only toggle selection — they no longer trigger
			// a fetch. The user picks one or more categories, then clicks
			// "See Your Events" to run the search.
			const category = $(e.currentTarget).data('category'); // '' means "All Events"
			this._setEngaged('filters');

			if (category === '') {
				this.state.selectedCategories = [];
				this.state.selectedTags = [];
				this.syncFilterTagCheckboxes();
				this._updateTagsPlaceholder();
			} else {
				const idx = this.state.selectedCategories.indexOf(category);
				if (idx === -1) {
					this.state.selectedCategories.push(category);
				} else {
					this.state.selectedCategories.splice(idx, 1);
				}
			}
			this.syncBubbleStates();
		},

		onSeeEventsClick: function () {
			this._setEngaged('filters');
			const from = this.$filterFrom.val();
			const to   = this.$filterTo.val();
			if (from && to && from > to) {
				this.showFilterError('Please check your date range — From must be before To.');
				return;
			}
			this.showFilterError('');

			if (typeof dataLayer !== 'undefined') {
				dataLayer.push({
					event: 'arts_finder_search',
					search_categories: this._namesFor(this.state.selectedCategories, this.state.categories),
					search_start_date: from || this._todayEastern(),
					search_end_date: to || '',
					search_tags: this._namesFor(this.state.selectedTags, this.state.tags)
				});
			}

			this._clearNlSearch();
			this.state.offset = 0;
			this.enterBrowseMode();
			this.fetchEvents();
		},

		enterBrowseMode: function () {
			this.state.mode = 'browse';
			this.$results.removeAttr('hidden');
			const top = this.$results.offset().top - 80;
			$('html, body').animate({ scrollTop: top }, 300);
		},

		onNlSubmit: function () {
			this._setEngaged('search');
			const query = this.$nlInput.val().trim();
			if (!query) return;

			if (typeof dataLayer !== 'undefined') {
				dataLayer.push({
					event: 'arts_finder_conversational_search',
					search_prompt: query
				});
			}

			this.state.lastQuery = query;
			this.state.mode      = 'search';
			this.state.offset    = 0;

			// Disable input and show spinner on the button
			this.$nlInput.prop('disabled', true);
			this.$nlSubmit
				.prop('disabled', true)
				.html('<span class="vp-events-cal__nl-spinner" aria-hidden="true"></span>');

			// Reveal results section, then scroll up to it
			this.$results.removeAttr('hidden');
			const top = this.$results.offset().top - 80;
			$('html, body').animate({ scrollTop: top }, 300);

			this.fetchSearch(query);
		},

		onPrevPage: function () {
			if (this.state.offset <= 0) return;
			this.state.offset -= this.state.limit;
			this._fetchCurrentMode();
			$('html, body').animate({ scrollTop: this.$results.offset().top - 80 }, 200);
		},

		onNextPage: function () {
			const totalPages  = Math.ceil(this.state.total / this.state.limit);
			const currentPage = Math.floor(this.state.offset / this.state.limit) + 1;
			if (currentPage >= totalPages) return;
			this.state.offset += this.state.limit;
			this._fetchCurrentMode();
			$('html, body').animate({ scrollTop: this.$results.offset().top - 80 }, 200);
		},

		_fetchCurrentMode: function () {
			if (this.state.mode === 'search') {
				this.fetchSearch(this.state.lastQuery);
			} else {
				this.fetchEvents();
			}
		},

		onRowClick: function (e) {
			// Ignore clicks on links or buttons inside the row (e.g. the event name link)
			if ($(e.target).closest('a, button').length) return;

			const $row  = $(e.currentTarget);
			const event = $row.data('event');
			if (!event) return;

			this.expandRow($row, event);
		},

		onRetry: function () {
			this._fetchCurrentMode();
		},

		// ─── Loading & error helpers ─────────────────────────────────────────────
		setLoading: function (loading) {
			this.state.loading = loading;
			this.$results.toggleClass('is-loading', loading);
			// aria-busy lets screen readers know the region is updating
			this.$results.attr('aria-busy', loading ? 'true' : 'false');
			if (loading) {
				this.$loader.removeAttr('hidden');
			} else {
				this.$loader.attr('hidden', '');
			}
			// Disable interactive controls while loading to prevent duplicate requests
			this.$seeEvents.prop('disabled', loading);
			this.$nlSubmit.prop('disabled', loading);
		},

		showFilterError: function (msg) {
			if (msg) {
				this.$filterError.text(msg).removeAttr('hidden');
			} else {
				this.$filterError.text('').attr('hidden', '');
			}
		},

		showApiError: function (show) {
			if (show) {
				this.$apiError.removeAttr('hidden');
				this.$empty.attr('hidden', '');
			} else {
				this.$apiError.attr('hidden', '');
			}
		},

	};

	$(document).ready(function () {
		VP.EventsCalendar.init();
	});

})(jQuery);
